import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Button, Form, Spinner } from 'react-bootstrap';
import {
  PencilFill, CheckLg, XLg, PlusCircle, Trash,
  Trophy, Award, StarFill, CashStack, Gem, PatchCheckFill, Bullseye, RocketTakeoff,
} from 'react-bootstrap-icons';
import { useAuth } from '../../contexts/SecurityContext';
import RoleGuard from '../security/RoleGuard';
import SectionControls from '../SectionControls';
import { updateAwardsSection } from '../../services/contentService';
import './AwardsBlock.scss';

// Small fixed icon set the admin can pick per award, mirroring the approach in
// CardGridBlock. Keeping the set small keeps the editor UI simple.
const ICON_OPTIONS = [
  { value: 'trophy', label: 'Trophy', Component: Trophy },
  { value: 'award', label: 'Award ribbon', Component: Award },
  { value: 'star', label: 'Star', Component: StarFill },
  { value: 'cash', label: 'Cash', Component: CashStack },
  { value: 'gem', label: 'Gem', Component: Gem },
  { value: 'check', label: 'Check badge', Component: PatchCheckFill },
  { value: 'bullseye', label: 'Bullseye', Component: Bullseye },
  { value: 'rocket', label: 'Rocket', Component: RocketTakeoff },
];

const ICON_BY_VALUE = Object.fromEntries(ICON_OPTIONS.map((i) => [i.value, i.Component]));

// Shared palette used for both the top accent bar and the value text.
const COLOR_OPTIONS = [
  { value: 'red', label: 'Red' },
  { value: 'darkred', label: 'Dark red' },
  { value: 'pink', label: 'Pink' },
  { value: 'gold', label: 'Gold' },
  { value: 'grey', label: 'Grey' },
  { value: 'black', label: 'Black' },
];

function defaultAward(label = 'FIRST PLACE', value = '$10,000', icon = 'trophy', accent = 'darkred', valueColor = 'red') {
  return { label, value, icon, accent, valueColor };
}

export function defaultAwardsData() {
  return {
    title: 'Awards',
    intro: '',
    awards: [
      defaultAward('FIRST PLACE', '$10,000', 'trophy', 'darkred', 'red'),
      defaultAward('SECOND PLACE', '$5,000', 'award', 'pink', 'black'),
    ],
    footnote: '',
  };
}

export function AwardsView({ data }) {
  const awards = Array.isArray(data?.awards) ? data.awards : [];
  // Lay 1/2/3 awards out across the row; 4+ wrap two-up on medium screens.
  const colSpan = awards.length === 1 ? 12 : awards.length === 3 ? 4 : 6;
  return (
    <div className="awards-block">
      {data?.title ? <h2 className="awards-block__title">{data.title}</h2> : null}
      {data?.intro ? <p className="awards-block__intro">{data.intro}</p> : null}
      <Row className="awards-block__awards g-3">
        {awards.map((award, idx) => {
          const Icon = ICON_BY_VALUE[award.icon] || Trophy;
          const accent = award.accent || 'red';
          const valueColor = award.valueColor || 'black';
          return (
            <Col key={idx} md={colSpan} className="awards-block__award-col">
              <div className={`awards-block__award awards-block__award--${accent}`}>
                <div className={`awards-block__icon-wrap awards-block__icon-wrap--${accent}`}>
                  <Icon className="awards-block__icon" />
                </div>
                <div className="awards-block__text">
                  {award.label ? <div className="awards-block__label">{award.label}</div> : null}
                  {award.value ? (
                    <div className={`awards-block__value awards-block__value--${valueColor}`}>
                      {award.value}
                    </div>
                  ) : null}
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
      {data?.footnote ? <p className="awards-block__footnote">{data.footnote}</p> : null}
    </div>
  );
}

function AwardsBlock({
  pageId, section, onMoveUp, onMoveDown, onDelete, isFirstSection, isLastSection,
}) {
  const { user } = useAuth();
  const initialData = useMemo(() => section?.data || defaultAwardsData(), [section]);
  const [data, setData] = useState(initialData);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => { setData(section?.data || defaultAwardsData()); }, [section]);

  async function handleSave() {
    setIsSaving(true);
    try {
      const modifiedBy = user?.attributes?.email || user?.username || 'user';
      await updateAwardsSection(pageId, section.position, data, modifiedBy);
      setIsEditing(false);
    } catch (e) {
      console.error('Error saving awards:', e);
      // eslint-disable-next-line no-alert
      alert('Failed to save section.');
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancel() { setData(section?.data || defaultAwardsData()); setIsEditing(false); }
  function updateField(field, value) { setData((prev) => ({ ...prev, [field]: value })); }
  function updateAward(idx, patch) {
    setData((prev) => {
      const next = { ...prev, awards: [...(prev.awards || [])] };
      next.awards[idx] = { ...next.awards[idx], ...patch };
      return next;
    });
  }
  function addAward() {
    setData((prev) => ({
      ...prev,
      awards: [...(prev.awards || []), defaultAward('THIRD PLACE', '$2,500', 'star', 'grey', 'black')],
    }));
  }
  function removeAward(idx) {
    setData((prev) => {
      const next = { ...prev, awards: [...(prev.awards || [])] };
      next.awards.splice(idx, 1);
      return next;
    });
  }

  if (isEditing) {
    return (
      <div className="awards-block awards-block--editing">
        <Row className="g-2 mb-3">
          <Col md={6}>
            <Form.Label>Section title</Form.Label>
            <Form.Control type="text" value={data.title || ''} onChange={(e) => updateField('title', e.target.value)} />
          </Col>
          <Col md={6}>
            <Form.Label>Intro paragraph (optional)</Form.Label>
            <Form.Control type="text" value={data.intro || ''} onChange={(e) => updateField('intro', e.target.value)} />
          </Col>
        </Row>

        {(data.awards || []).map((award, idx) => (
          <div key={idx} className="awards-block__editor-award">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Award {idx + 1}</strong>
              <Button variant="outline-danger" size="sm" onClick={() => removeAward(idx)}>
                <Trash /> Remove
              </Button>
            </div>
            <Row className="g-2">
              <Col md={4}>
                <Form.Label>Label</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="FIRST PLACE"
                  value={award.label || ''}
                  onChange={(e) => updateAward(idx, { label: e.target.value })}
                />
              </Col>
              <Col md={4}>
                <Form.Label>Value</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="$10,000"
                  value={award.value || ''}
                  onChange={(e) => updateAward(idx, { value: e.target.value })}
                />
              </Col>
              <Col md={4}>
                <Form.Label>Icon</Form.Label>
                <Form.Select value={award.icon || 'trophy'} onChange={(e) => updateAward(idx, { icon: e.target.value })}>
                  {ICON_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>Accent colour (top bar + icon)</Form.Label>
                <Form.Select value={award.accent || 'red'} onChange={(e) => updateAward(idx, { accent: e.target.value })}>
                  {COLOR_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>Value colour</Form.Label>
                <Form.Select value={award.valueColor || 'black'} onChange={(e) => updateAward(idx, { valueColor: e.target.value })}>
                  {COLOR_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Form.Select>
              </Col>
            </Row>
          </div>
        ))}

        <Button variant="outline-primary" size="sm" onClick={addAward} className="mb-3">
          <PlusCircle /> Add award
        </Button>

        <Form.Group className="mb-2">
          <Form.Label>Note below the awards (optional)</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            placeholder="e.g. Prize amounts are subject to final agreement."
            value={data.footnote || ''}
            onChange={(e) => updateField('footnote', e.target.value)}
          />
        </Form.Group>

        <div className="awards-block__editor-actions">
          <Button variant="success" onClick={handleSave} disabled={isSaving}>
            {isSaving ? (<><Spinner as="span" animation="border" size="sm" className="me-2" />Saving…</>) : (<><CheckLg /> Save</>)}
          </Button>
          <Button variant="secondary" onClick={handleCancel} disabled={isSaving}><XLg /> Cancel</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="awards-block-wrapper">
      <AwardsView data={data} />
      <RoleGuard role="admin">
        <div className="admin-controls">
          <Button variant="outline-secondary" size="sm" className="edit-btn" onClick={() => setIsEditing(true)}>
            <PencilFill /> Edit awards
          </Button>
          <SectionControls onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDelete={onDelete} isFirstSection={isFirstSection} isLastSection={isLastSection} />
        </div>
      </RoleGuard>
    </div>
  );
}

export default AwardsBlock;
